import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import type { BusinessLabel } from '../types';

export interface ConnectTargetOrg {
  /** Businesses a new source may be filed under — the active workspace's labels. */
  orgs: BusinessLabel[];
  targetOrgId: string | null;
  setTargetOrgId: (id: string) => void;
  targetOrgName: string | null;
  /** False when there's nothing to choose — callers hide the picker entirely. */
  hasChoice: boolean;
}

/**
 * The destination business for a new data source (R9).
 *
 * Explicit end-to-end: auto-filled only when the workspace has exactly one
 * business, otherwise the caller must pick — there is no ambient "current"
 * business to fall back on. Scoped to the ACTIVE workspace, which is the
 * billing, quota and grant boundary the backend already ran CREATE_DATA_SOURCE
 * against; targeting another workspace would need its own role + entitlement
 * check, so switch workspaces instead.
 */
export function useConnectTargetOrg(): ConnectTargetOrg {
  const { organizations: orgs } = useAuth();
  const [override, setOverride] = useState<string | null>(null);

  // An override only holds while it stays valid (a workspace switch reflows
  // `orgs` and drops a now-foreign pick).
  const picked = override && orgs.some((o) => o.id === override) ? override : null;
  const targetOrgId = picked ?? (orgs.length === 1 ? orgs[0].id : null);

  return {
    orgs,
    targetOrgId,
    setTargetOrgId: setOverride,
    targetOrgName: orgs.find((o) => o.id === targetOrgId)?.name ?? null,
    hasChoice: orgs.length > 1,
  };
}
